import { type ReactNode, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { FloatingNav } from "./FloatingNav";
import { MobileBottomNav } from "./MobileBottomNav";
import { AppRailFixed } from "./AppRailFixed";
import { AppRailRight, RIGHT_RAIL_WIDTH } from "./AppRailRight";
import { DetailPageChatProvider } from "@/contexts/DetailPageChatContext";
import { DetailPageShareProvider } from "@/contexts/DetailPageShareContext";
import { DetailPageSidebarProvider } from "@/contexts/DetailPageSidebarContext";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { RAIL_WIDTH } from "./ErpSidebar";

const DESKTOP_BREAKPOINT = "(min-width: 768px)";

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT);
  const mainRef = useRef<HTMLElement>(null);
  // Doc detail pages get a white canvas so they read distinctly from the
  // gray-ish sidebar (list pages keep the gray-50 canvas). LINE OA pages are a
  // white LINE-branded surface, so they use the white canvas too.
  const { pathname } = useLocation();
  const isDocPage = pathname.startsWith("/doc/");
  const isLineOAPage = pathname === "/line-oa" || pathname.startsWith("/line-oa/");
  // Customer detail (pretty route /customers/:name) is a detail page — give it the
  // same white canvas as /doc/ detail pages. The /customers list already renders
  // its own white shell, so we only match the detail sub-path here.
  const isCustomerDetailPage = pathname.startsWith("/customers/");
  // Pricing Rule detail (the coupon page) is a custom detail page — give it the
  // same white canvas as /doc/ detail pages. Trailing slash matches only the
  // detail sub-paths (/inventory/pricing-rule/PRLE-…), not the bare list.
  const isPricingRuleDetail = pathname.startsWith("/inventory/pricing-rule/");
  const isWhiteCanvas = isDocPage || isLineOAPage || isCustomerDetailPage || isPricingRuleDetail;
  // Support ticket detail is a chat surface: its reply composer is sticky to the
  // bottom of the screen, so main must not add desktop bottom padding underneath
  // it (mobile keeps pb-20 to clear the fixed MobileBottomNav). Excludes the
  // /support list and /support/new compose page.
  const isSupportTicketPage =
    pathname.startsWith("/support/") && pathname !== "/support/new";

  // Broadcast scroll events from main container so MobileBottomNav can react
  useEffect(() => {
    const el = mainRef.current;
    if (!el || isDesktop) return;
    const onScroll = () => {
      window.dispatchEvent(new CustomEvent('panel-scroll', { detail: { scrollTop: el.scrollTop } }));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [isDesktop]);

  return (
    <DetailPageChatProvider>
      <DetailPageShareProvider>
        <DetailPageSidebarProvider>
          <div className="bg-gray-50 dark:bg-[#000000] h-screen flex flex-col overflow-hidden">
            {/* Left app rail — always visible on desktop */}
            {isDesktop && <AppRailFixed />}

            {/* Right app rail — utility apps (Chat, Notes) */}
            {isDesktop && <AppRailRight />}

            <FloatingNav />

            <main
              ref={mainRef}
              className={cn(
                "flex-1 min-h-0 overflow-y-auto overflow-x-hidden pt-0 scrollbar-hide",
                isSupportTicketPage ? "pb-20 md:pb-0" : "pb-20 md:pb-6",
                isWhiteCanvas && "bg-white dark:bg-neutral-950"
              )}
              style={isDesktop ? { paddingLeft: `${RAIL_WIDTH}px`, paddingRight: `${RIGHT_RAIL_WIDTH}px` } : undefined}
            >
              {children}
            </main>

            {!isDesktop && <MobileBottomNav />}
          </div>
        </DetailPageSidebarProvider>
      </DetailPageShareProvider>
    </DetailPageChatProvider>
  );
}
